import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../layouts/AuthLayout';
import { Button, Field, Notice } from '../../components/ui';
import { authErrorMessage, finishPasswordReset, startPasswordReset } from '../../lib/auth';

/** j***@school.org: enough to recognise, without showing the whole address. */
function mask(email: string) {
  const [user, domain] = email.split('@');
  if (!domain) return email;
  return `${user.slice(0, 1)}${'*'.repeat(Math.max(2, Math.min(user.length - 1, 6)))}@${domain}`;
}

const RESEND_SECONDS = 30;

/** Two steps, like the app: email, then the 6-digit code and a new password. */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const initialEmail = (useLocation().state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(initialEmail);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setSentTo((await startPasswordReset(email)) ?? mask(email));
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(null);
    setResent(false);
    try {
      await startPasswordReset(email);
      setResent(true);
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(authErrorMessage(err));
    }
  };

  const reset = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await finishPasswordReset(email, code, password);
      navigate('/sign-in', { replace: true, state: { reset: true } });
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={sentTo ? 'Check your email' : 'Reset your password'}
      subtitle={
        sentTo
          ? `We sent a 6-digit code to ${sentTo}. It works for one hour.`
          : 'We’ll email you a 6-digit code to set a new password.'
      }
    >
      {sentTo ? (
        <form className="auth-form" onSubmit={reset} noValidate>
          {error ? <Notice tone="error">{error}</Notice> : null}
          {resent ? <Notice tone="success">We sent a new code. Use the newest one.</Notice> : null}
          <p className="auth-hint">Not there after a minute or two? Check your spam or junk folder: school and college filters sometimes hold automated email.</p>
          <Field id="reset-code" label="Code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
          <Field
            id="reset-password"
            label="New password"
            type="password"
            autoComplete="new-password"
            hint="At least 8 characters, with upper and lower case letters, a number and a symbol."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" size="lg" block loading={busy}>
            Set new password
          </Button>
          <Button type="button" variant="ghost" onClick={resend} disabled={cooldown > 0}>
            {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setSentTo(null)}>
            Use a different email
          </Button>
        </form>
      ) : (
        <form className="auth-form" onSubmit={sendCode} noValidate>
          {error ? <Notice tone="error">{error}</Notice> : null}
          <Field id="reset-email" label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          <Button type="submit" size="lg" block loading={busy}>
            Send code
          </Button>
          <p className="auth-hint">Sign in with Google or Apple? There&rsquo;s no EducAitor password to reset: use the Google or Apple button on the sign-in page.</p>
        </form>
      )}
      <p className="auth-foot">
        <Link to="/sign-in">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}
